import React, { useState, useMemo, useEffect } from "react";
import COLORS from "../../constants/colors";
import { learningApi, wordApi } from "../../utils/api";
import {
  contentSectionStyle,
  listCardStyle,
  mainCardStyle,
  pageStyle,
  sectionTitleStyle,
  smallTopBtnStyle,
} from "../../styles/commonStyles";

export default function StatisticsPage({
  state,
  onGoHome,
  showTempMessage = () => {},
  openWordDetail = () => {},
  speakFrench = () => {},
}) {
  // ==============================================
  // 🔥 修复：强制兜底初始值，即使接口失败也能渲染分类项
  // ==============================================
  const defaultStats = {
    total_learn: 0,
    total_review: 0,
    ebbinghaus_stats: [
      {"label": "今日到期", "count": 0},
      {"label": "一天后到期", "count": 0},
      {"label": "三天后到期", "count": 0},
      {"label": "七天后到期", "count": 0},
      {"label": "三十天后到期", "count": 0}
    ],
    continuous_days: 0,
    daily_stats: []
  };

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(defaultStats);
  const [starredWords, setStarredWords] = useState([]);
  const [activeCurveLabel, setActiveCurveLabel] = useState(null);
  const [curveWords, setCurveWords] = useState([]);

  // ==============================================
  // 从后端拉取数据，失败也不会覆盖默认值
  // ==============================================
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [statsData, favData] = await Promise.all([
          learningApi.getLearningStats(),
          wordApi.getFavorites()
        ]);

        // 🔥 修复：合并接口数据和默认值，避免接口字段缺失导致渲染失败
        setStats({
          ...defaultStats,
          ...statsData,
          ebbinghaus_stats: statsData?.ebbinghaus_stats || defaultStats.ebbinghaus_stats
        });
        setStarredWords(favData?.favorites || []);
      } catch (err) {
        console.error("数据加载失败，使用默认数据", err);
        setStats(defaultStats); // 接口失败时，强制用默认值兜底
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const maxCurve = useMemo(() => {
    const counts = stats.ebbinghaus_stats.map(item => item.count);
    return Math.max(...counts, 1);
  }, [stats.ebbinghaus_stats]);

  // ==============================================
  // 🔥 修复：点击事件绑定 + 反馈优化
  // ==============================================
  const handleStatClick = (type, value, label = "") => {
    if (type === "curve") {
      showTempMessage(`你点击了【${label}】，正在从后端获取单词列表...`, 1200);
      (async () => {
        try {
          const resp = await learningApi.getProgress(`?bucket=${encodeURIComponent(label)}`);
          let wordsList = [];
          if (resp && Array.isArray(resp.words)) {
            wordsList = resp.words.map(w => ({ id: w.word_id || w.id, word: w.word || w.text || "", translation: w.translation || w.meaning || "", phonetic: w.phonetic || "" }));
          }
          if (wordsList.length > 0) {
            setCurveWords(wordsList);
            setActiveCurveLabel(label);
            showTempMessage(`已加载 ${wordsList.length} 个待复习单词`, 1200);
          } else {
            // 回退到原有模拟数据
            if (value > 0) {
              setCurveWords([
                { id: 1, word: "bonjour", translation: "你好", phonetic: "[bɔ̃ʒuʁ]" },
                { id: 2, word: "au revoir", translation: "再见", phonetic: "[o ʁəvwaʁ]" }
              ]);
              setActiveCurveLabel(label);
              showTempMessage("后端未返回单词列表，已使用本地兜底数据", 1600);
            } else {
              setCurveWords([]);
              setActiveCurveLabel(null);
            }
          }
        } catch (err) {
          console.error("获取曲线单词失败，使用本地兜底：", err);
          showTempMessage("无法获取后端数据，使用本地兜底", 1500);
          if (value > 0) {
            setCurveWords([
              { id: 1, word: "bonjour", translation: "你好", phonetic: "[bɔ̃ʒuʁ]" },
              { id: 2, word: "au revoir", translation: "再见", phonetic: "[o ʁəvwaʁ]" }
            ]);
            setActiveCurveLabel(label);
          } else {
            setCurveWords([]);
            setActiveCurveLabel(null);
          }
        }
      })();
    }
  };

  const closeCurveWords = () => {
    setActiveCurveLabel(null);
    setCurveWords([]);
  };

  if (loading) {
    return (
      <div style={pageStyle()}>
        <div style={mainCardStyle()}>
          <div style={{ textAlign: "center", padding: "50px 0" }}>加载学习数据中...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle()}>
      <div style={mainCardStyle()}>
        {/* 头部 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <div style={{ fontSize: "1.35rem", fontWeight: 800, color: COLORS.text }}>学习情况</div>
          <button onClick={onGoHome} style={smallTopBtnStyle("#E9EEF5")}>⌂</button>
        </div>

        {/* 1. 艾宾浩斯遗忘曲线 -  修复：强制渲染5个分类项 */}
        <section style={contentSectionStyle()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={sectionTitleStyle()}>1. 艾宾浩斯遗忘曲线 </div>
            <button 
              onClick={() => window.location.href = '/ebbinghaus-table'}
              style={{
                background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.gold})`,
                color: '#fff',
                border: 'none',
                padding: '0.4rem 1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600',
              }}
            >
              查看详细计划表 →
            </button>
          </div>
          <div style={{ color: COLORS.subtext, marginBottom: "1rem" }}>
            学习后自动生成复习计划，随时间推移更新分类（点击查看具体单词）
          </div>

          {/* 🔥 修复：确保map循环一定会执行，显示5个分类项 */}
          <div style={{ display: "grid", gap: "0.9rem" }}>
            {stats.ebbinghaus_stats.map((item, index) => (
              <div 
                key={index}
                style={{ 
                  cursor: "pointer",
                  backgroundColor: activeCurveLabel === item.label ? "#f0f7ff" : "transparent",
                  padding: "0.5rem",
                  borderRadius: "8px",
                  transition: "background-color 0.2s ease",
                  border: "1px solid #eee"
                }}
                onClick={() => handleStatClick("curve", item.count, item.label)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = activeCurveLabel === item.label ? "#f0f7ff" : "transparent"}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                  <span style={{ color: COLORS.text, fontWeight: item.count > 0 ? 700 : 400 }}>{item.label}</span>
                  <span style={{ color: item.count > 0 ? COLORS.blue : COLORS.subtext, fontWeight: 700 }}>{item.count}</span>
                </div>
                <div style={{ height: "10px", background: "#EEF1F4", borderRadius: "999px", overflow: "hidden" }}>
                  <div style={{ width: `${(item.count / maxCurve) * 100}%`, height: "100%", background: `linear-gradient(90deg, ${COLORS.blue}, ${COLORS.gold})` }} />
                </div>
              </div>
            ))}
          </div>

          {/* 单词列表弹窗 */}
          {activeCurveLabel && curveWords.length > 0 && (
            <div style={{ marginTop: "1rem", padding: "1rem", background: "#f8f9fa", borderRadius: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.8rem" }}>
                <h4>{activeCurveLabel} 待复习单词 ({curveWords.length})</h4>
                <button onClick={closeCurveWords} style={{ background: "none", border: "none", cursor: "pointer" }}>✕</button>
              </div>
              {curveWords.map((word) => (
                <div key={word.id} style={{ ...listCardStyle(), padding: "0.8rem" }} onClick={() => openWordDetail(word.id)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{word.word}</div>
                    <div style={{ color: COLORS.subtext, fontSize: "0.9rem" }}>{word.translation} {word.phonetic}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); speakFrench(word.word); }} style={{ background: "transparent", border: "none", color: COLORS.gold }}>🔊</button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 2. 每日学习情况 */}
        <section style={contentSectionStyle()}>
          <div style={sectionTitleStyle()}>2. 每日学习情况</div>
          <div style={{ color: COLORS.subtext }}>累计学习：{stats.total_learn} · 累计复习：{stats.total_review}</div>
        </section>

        {/* 3. 当前学习状态概览 */}
        <section style={contentSectionStyle()}>
          <div style={sectionTitleStyle()}>3. 当前学习状态概览</div>
          <div style={{ display: "grid", gap: "0.8rem" }}>
            <div style={listCardStyle()}>
              <div style={{ fontWeight: 700 }}>累计签到</div>
              <div style={{ color: COLORS.subtext, fontSize: "0.9rem" }}>当前连续签到天数</div>
              <div style={{ color: COLORS.blue, fontWeight: 800 }}>{stats.continuous_days}</div>
            </div>
            <div style={listCardStyle()}>
              <div style={{ fontWeight: 700 }}>星标单词数</div>
              <div style={{ color: COLORS.subtext, fontSize: "0.9rem" }}>已加入单词本的重点词汇</div>
              <div style={{ color: COLORS.gold, fontWeight: 800 }}>{starredWords.length}</div>
            </div>
          </div>
        </section>

        {/* 4. 我的星标单词 */}
        <section style={contentSectionStyle()}>
          <div style={sectionTitleStyle()}>4. 我的星标单词</div>
          <div style={{ color: COLORS.subtext, marginBottom: "1rem", fontSize: "0.9rem" }}>点击单词查看详情，点击喇叭图标播放发音</div>
          {starredWords.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2rem 0", color: COLORS.subtext }}>📖 暂无星标单词<br/>学习过程中点击⭐可收藏重点单词</div>
          ) : (
            <div style={{ display: "grid", gap: "0.8rem" }}>
              {starredWords.map((word) => (
                <div key={word.id} style={{ ...listCardStyle(), padding: "0.8rem" }} onClick={() => openWordDetail(word.id)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{word.word}</div>
                    <div style={{ color: COLORS.subtext, fontSize: "0.9rem" }}>{word.translation}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); speakFrench(word.word); }} style={{ background: "transparent", border: "none", color: COLORS.gold }}>🔊</button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}